import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

const MAX_SELFIE_EDGE_PX = 960;
const SELFIE_JPEG_QUALITY = 0.82;

function base64ToFile(base64String, mimeType = "image/jpeg") {
  const binary = atob(base64String);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], "team-selfie.jpg", { type: mimeType });
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read photo"));
    };
    img.src = url;
  });
}

/** Resize/compress for fast upload — team selfies only need a small portrait thumbnail. */
export async function compressSelfieFile(file) {
  if (!file || !(file instanceof Blob)) {
    throw new Error("No photo to compress");
  }

  const img = await loadImageFromFile(file);
  const scale = Math.min(1, MAX_SELFIE_EDGE_PX / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not prepare photo");
  }
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("Could not compress photo"))),
      "image/jpeg",
      SELFIE_JPEG_QUALITY
    );
  });

  return new File([blob], "team-selfie.jpg", { type: "image/jpeg" });
}

export function formatSelfieFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function pickTeamSelfieFromSource(source) {
  const photo = await Camera.getPhoto({
    quality: 75,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source,
    saveToGallery: false,
  });

  if (!photo.base64String) {
    throw new Error("No photo selected");
  }

  const rawFile = base64ToFile(photo.base64String);
  return compressSelfieFile(rawFile);
}

/** Open the device camera; on iOS Simulator falls back to photo library. */
export async function pickTeamSelfieFromCamera() {
  try {
    return await pickTeamSelfieFromSource(CameraSource.Camera);
  } catch (err) {
    if (isSelfiePickCancelled(err)) throw err;
    if (isSimulatorCameraUnavailable(err)) {
      return pickTeamSelfieFromSource(CameraSource.Photos);
    }
    throw err;
  }
}

/** Open the device photo library only — no generic “Choose File” option. */
export function pickTeamSelfieFromLibrary() {
  return pickTeamSelfieFromSource(CameraSource.Photos);
}

export function isSelfiePickCancelled(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("cancel") || message.includes("dismiss");
}

export function isCameraPluginUnavailable(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("not implemented") || message.includes("plugin");
}

export function isSimulatorCameraUnavailable(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return (
    message.includes("simulator") ||
    message.includes("no camera") ||
    message.includes("camera unavailable")
  );
}
